import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { validacionService } from '../../services/validacion';
import toast from 'react-hot-toast';
import { CheckCircle2, XCircle, Clock, ShieldQuestion } from 'lucide-react';

const ESTADO_CONFIG = {
    pendiente: { label: 'Pendiente de validación', icon: Clock, cls: 'bg-gray-100 text-gray-600 border-gray-200' },
    aprobado: { label: 'Aprobado', icon: CheckCircle2, cls: 'bg-green-50 text-green-700 border-green-200' },
    rechazado: { label: 'Rechazado', icon: XCircle, cls: 'bg-red-50 text-red-700 border-red-200' },
};

/**
 * Panel de aprobación/rechazo de una sesión de inspección completa (período + CT
 * cuando aplica). Visible en cualquier módulo; solo admin/validador pueden actuar.
 */
const PanelValidacion = ({ modulo, sesion, ct = null }) => {
    const { puedeValidar } = useAuth();
    const [estado, setEstado] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [comentario, setComentario] = useState('');
    const [mostrarComentario, setMostrarComentario] = useState(null); // 'aprobado' | 'rechazado' | null

    const cargar = async () => {
        setCargando(true);
        try {
            const data = await validacionService.obtenerEstado(modulo, sesion, ct);
            setEstado(data);
            setComentario(data.comentario || '');
        } catch {
            /* silencioso: no bloquea la página si falla */
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, [modulo, sesion, ct]);

    const handleValidar = async (nuevoEstado) => {
        setEnviando(true);
        try {
            const data = await validacionService.validar(modulo, sesion, nuevoEstado, ct, comentario || null);
            setEstado(data);
            setMostrarComentario(null);
            toast.success(nuevoEstado === 'aprobado' ? 'Sesión aprobada' : 'Sesión rechazada');
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Error al validar');
        } finally {
            setEnviando(false);
        }
    };

    if (cargando || !estado) return null;

    const config = ESTADO_CONFIG[estado.estado] || ESTADO_CONFIG.pendiente;
    const Icon = config.icon;

    return (
        <div className={`mb-4 rounded-xl border p-4 ${config.cls}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5" />
                    <div>
                        <p className="text-sm font-semibold">{config.label}</p>
                        {estado.validado_por && (
                            <p className="text-xs opacity-75">
                                Por {estado.validado_por}
                                {estado.fecha_validacion ? ` — ${new Date(estado.fecha_validacion).toLocaleString('es-PE')}` : ''}
                            </p>
                        )}
                    </div>
                </div>

                {puedeValidar() && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setMostrarComentario(mostrarComentario === 'aprobado' ? null : 'aprobado')}
                            disabled={enviando}
                            className="flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-60"
                        >
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                            Aprobar
                        </button>
                        <button
                            onClick={() => setMostrarComentario(mostrarComentario === 'rechazado' ? null : 'rechazado')}
                            disabled={enviando}
                            className="flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60"
                        >
                            <XCircle className="h-3.5 w-3.5 mr-1" />
                            Rechazar
                        </button>
                    </div>
                )}
            </div>

            {puedeValidar() && mostrarComentario && (
                <div className="mt-3 flex gap-2">
                    <input
                        type="text"
                        value={comentario}
                        onChange={(e) => setComentario(e.target.value)}
                        placeholder="Comentario (opcional)..."
                        className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <button
                        onClick={() => handleValidar(mostrarComentario)}
                        disabled={enviando}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                        Confirmar
                    </button>
                </div>
            )}

            {estado.comentario && !mostrarComentario && (
                <p className="text-xs mt-2 italic opacity-80">
                    <ShieldQuestion className="h-3 w-3 inline mr-1" />
                    {estado.comentario}
                </p>
            )}
        </div>
    );
};

export default PanelValidacion;
